/**
 * The two requests Jot makes, and the only place the bearer token is handled.
 *
 * Everything here is shaped so a caller never has to catch: every outcome —
 * including a refused endpoint, a network that is not there and a `409` — is a
 * value. A sync path that could throw would have to decide what a half-failed
 * push means to Sync State at every call site, and there would be several.
 *
 * Two rules about the token, both load-bearing (ADR-0003). It travels in an
 * `Authorization` header and never in a URL, so it cannot end up in a proxy
 * access log or a browser history entry. And nothing here logs: not the token,
 * not the header, not a response body. What the caller gets to report is one of
 * the outcomes below and nothing more.
 */

import { MAX_PUSH_BYTES, MAX_SCRATCHPAD_BYTES } from '@shared/scratchpad-limits'
import { isTimestamp, MAX_PUSHED_BY_LENGTH } from '@shared/sync-state'
import type { ScratchpadPush, ScratchpadRecord } from '@shared/sync-wire'
import { scratchpadEndpoint, type EndpointProblem } from './endpoint'

/**
 * Long enough for a slow phone tether, short enough that a black-holed
 * connection cannot hold the sync path open indefinitely — a request that never
 * settles would leave the engine believing a push is still in flight.
 */
const REQUEST_TIMEOUT_MS = 15_000

/** Outcomes that say nothing about the Scratchpad, only about the attempt. */
export type SyncFailure =
  /** Nothing was sent: this endpoint is not one the token may travel over. */
  | { kind: 'refused'; problem: EndpointProblem }
  /** The token is wrong, or the server has since rotated it. */
  | { kind: 'unauthorized' }
  /** No answer: offline, DNS, TLS, a timeout, or a redirect we would not follow. */
  | { kind: 'unreachable' }
  /**
   * An answer there is nothing to be done with — a proxy error page, a 429.
   *
   * Usually a status this contract does not describe, and in one case a status it
   * does: an ordinary push that lost the same race twice is reported this way rather
   * than recorded as either outcome, because it established neither (`recordPush` in
   * `./engine`). Both mean the same thing to the user and to the next pass — the
   * attempt failed, nothing about the Scratchpad was established, nothing was
   * written — which is why they are one outcome and not two.
   */
  | { kind: 'rejected'; httpStatus: number }
  /** The right status carrying the wrong shape. Never treated as content. */
  | { kind: 'malformed' }
  /**
   * The Jot Server's Scratchpad is more bytes than one is allowed to be, so nothing
   * was read.
   *
   * Its own outcome rather than one more `malformed`, because it is a different
   * problem with a different remedy and one the user can act on: a corrupt answer
   * is somebody else's bug, while this one names a size and a setting. It is also
   * the one failure here that does not clear itself — a Scratchpad too large to read
   * stays too large, so a device that meets it never pairs and never pushes, and
   * saying "something Jot could not read" would leave that permanent state looking
   * like a passing glitch.
   *
   * A conforming Jot Server can no longer produce it: it refuses to start above the
   * push ceiling, which is the response cap less the room a `GET` needs. What is left
   * is a server that is not one, or something in front of it padding the answer —
   * neither of which this client gets to assume away.
   */
  | { kind: 'oversized' }
  /**
   * *This device's* Scratchpad is too large to push — the mirror of `oversized`, and
   * now the reachable direction.
   *
   * Distinct from `rejected` for the reason `oversized` is distinct from `malformed`,
   * and more urgently: a `413` rendered as a bare HTTP number is indistinguishable
   * from a `429`, from a proxy's `502` and from any other status this contract does
   * not describe, and unlike those it never clears itself, because a scratchpad only
   * grows. The device stops syncing permanently while naming neither the cause nor
   * the remedy.
   *
   * `bytes` is the size of the whole request, which is what both caps measure.
   * `limit` is the ceiling when this device refused the push itself, and null when a
   * Jot Server refused it under a configured cap it did not disclose.
   */
  | { kind: 'too-large'; bytes: number; limit: number | null }

export type ReadOutcome =
  | { kind: 'record'; record: ScratchpadRecord }
  /** The `404`: nothing has ever been pushed, so no device has to yield. */
  | { kind: 'empty' }
  | SyncFailure

export type PushOutcome =
  | { kind: 'accepted'; rev: number }
  /** The `409`, carrying the record that won in full. */
  | { kind: 'conflict'; record: ScratchpadRecord }
  | SyncFailure

export interface ScratchpadClient {
  read(): Promise<ReadOutcome>
  push(body: string, basedOnRev: number, id: string): Promise<PushOutcome>
}

export interface ScratchpadClientOptions {
  /** As configured, not as resolved: refusing it is this module's job. */
  endpoint: string
  token: string
  /** Asserted as `pushedBy`, and a display hint on arrival (ADR-0003). */
  deviceLabel: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Project a response onto `ScratchpadRecord`, field by field.
 *
 * A Jot Server is self-hosted and trusted with the Scratchpad, which is not the
 * same as being trusted to be correct: this record ends up in Sync State and, at
 * Resolution, decides which body survives. Every field is therefore held to the
 * same standard, `updatedAt` included — it is persisted and then handed to a
 * formatter that throws on a value `Date` cannot represent, and the renderer must
 * not depend on a server for whether it can paint. `pushedBy` is capped to the
 * length the server caps it at, by code point rather than code unit, so a label
 * ending in an emoji is not cut in half.
 */
function asRecord(value: unknown): ScratchpadRecord | null {
  if (!isRecord(value)) return null

  const { id, body, rev, updatedAt, pushedBy } = value
  if (typeof id !== 'string' || id === '') return null
  if (typeof body !== 'string') return null
  if (typeof rev !== 'number' || !Number.isSafeInteger(rev) || rev < 1) return null
  if (!isTimestamp(updatedAt)) return null

  return {
    id,
    body,
    rev,
    updatedAt,
    pushedBy:
      typeof pushedBy === 'string'
        ? Array.from(pushedBy).slice(0, MAX_PUSHED_BY_LENGTH).join('')
        : '',
  }
}

/**
 * What came back, or which way reading it failed.
 *
 * Two failures and not one, because the two are told apart all the way out to the
 * status the user reads: over the cap is a Scratchpad this device can never read,
 * and everything else is an answer that did not arrive intact.
 */
type CappedBody = { ok: true; text: string } | { ok: false; oversized: boolean }

/**
 * Read a response, refusing one larger than a Scratchpad is allowed to be.
 *
 * The mirror image of the server's own `readBody`, and for the reason this
 * module's own header gives: the answer is the direction that is not trusted.
 * `content-length` is checked first because refusing on a claim costs nothing,
 * then the bytes are counted as they arrive, because the claim can be absent or a
 * lie. `response.json()` cannot do either — it buffers whatever arrives, and what
 * arrives is persisted.
 *
 * The stream is cancelled rather than abandoned when the cap is hit, or the
 * connection stays open with a sender still writing into it.
 */
async function readCapped(response: Response): Promise<CappedBody> {
  const declared = Number(response.headers.get('content-length'))
  if (Number.isFinite(declared) && declared > MAX_SCRATCHPAD_BYTES) {
    return { ok: false, oversized: true }
  }
  if (response.body === null) return { ok: false, oversized: false }

  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let size = 0

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      size += value.byteLength
      if (size > MAX_SCRATCHPAD_BYTES) {
        await reader.cancel()
        return { ok: false, oversized: true }
      }
      chunks.push(value)
    }
  } catch {
    // The timeout fired mid-body, or the connection went away. Same answer as a
    // truncated one: there is nothing here to act on.
    return { ok: false, oversized: false }
  }

  const merged = new Uint8Array(size)
  let at = 0
  for (const chunk of chunks) {
    merged.set(chunk, at)
    at += chunk.byteLength
  }

  return { ok: true, text: new TextDecoder().decode(merged) }
}

/**
 * The parsed answer, or the failure to report instead of it.
 *
 * Wrapped in `{ value }` rather than returned bare, because the answer may legally
 * *be* `null` — `JSON.parse('null')` succeeds — and a bare null would have to mean
 * both "that parsed to nothing" and "reading it failed", which is the conflation this
 * whole change is undoing one level up.
 */
async function parseJson(response: Response): Promise<{ value: unknown } | SyncFailure> {
  const body = await readCapped(response)
  if (!body.ok) return { kind: body.oversized ? 'oversized' : 'malformed' }

  try {
    return { value: JSON.parse(body.text) as unknown }
  } catch {
    // A truncated body, or an HTML error page from something in front of the
    // server. Either way there is nothing here to act on.
    return { kind: 'malformed' }
  }
}

export function createScratchpadClient({
  endpoint,
  token,
  deviceLabel,
}: ScratchpadClientOptions): ScratchpadClient {
  const resolved = scratchpadEndpoint(endpoint)

  async function request(init: RequestInit): Promise<Response | SyncFailure> {
    if (!resolved.ok) return { kind: 'refused', problem: resolved.problem }

    try {
      return await fetch(resolved.url, {
        ...init,
        headers: {
          ...init.headers,
          authorization: `Bearer ${token}`,
        },
        // Nothing about the Scratchpad is cacheable, and no ambient cookie has
        // any business authenticating a request the token already authenticates.
        cache: 'no-store',
        credentials: 'omit',
        // The Jot Server never redirects, so a redirect is a misconfigured proxy
        // at best — and following one would forward the token to whatever host it
        // names.
        redirect: 'error',
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      })
    } catch {
      // `fetch` rejects for offline, DNS, TLS, CORS, the abort above and the
      // refused redirect. The error is deliberately not logged: its message can
      // quote the URL, and the caller only needs to know nothing arrived.
      return { kind: 'unreachable' }
    }
  }

  return {
    async read() {
      const response = await request({ method: 'GET' })
      if (!(response instanceof Response)) return response

      if (response.status === 401) return { kind: 'unauthorized' }
      if (response.status === 404) return { kind: 'empty' }
      if (response.status !== 200) return { kind: 'rejected', httpStatus: response.status }

      const parsed = await parseJson(response)
      if (!('value' in parsed)) return parsed
      const record = asRecord(parsed.value)
      return record === null ? { kind: 'malformed' } : { kind: 'record', record }
    },

    async push(body, basedOnRev, id) {
      const payload: ScratchpadPush = {
        id,
        body,
        // The revision this push is *based on*: `0` from a device that has never
        // seen the server's Scratchpad, which is what makes the first push an
        // ordinary push against an empty server.
        rev: basedOnRev,
        pushedBy: deviceLabel,
      }

      const json = JSON.stringify(payload)
      // Measured the way the server measures it: its cap is on the whole request
      // body, so checking the markdown alone would pass here and meet the `413` this
      // check exists to spend nothing on. `>` and not `>=`, because the server
      // accepts a request of exactly its cap — a client refusing what the server
      // would take is the same one-sided disagreement the shared caps prevent.
      //
      // Locally rather than only on the answer, because the answer costs a round trip
      // of an oversized upload every time the user types, and because the ceiling is
      // the one number this device can be sure of.
      const bytes = new TextEncoder().encode(json).byteLength
      if (bytes > MAX_PUSH_BYTES) return { kind: 'too-large', bytes, limit: MAX_PUSH_BYTES }

      const response = await request({
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        // The string, not the bytes above: `fetch` sets `content-length` to its UTF-8
        // length, so what was measured is what the server counts.
        body: json,
      })
      if (!(response instanceof Response)) return response

      if (response.status === 401) return { kind: 'unauthorized' }

      // Under the ceiling and still refused, so this server's `JOT_MAX_BODY_BYTES` is
      // set lower than the ceiling — which is allowed, and is a number no client can
      // know in advance. Same problem and same remedy as the local refusal above, so
      // the same outcome rather than a bare status: `limit` is null because the server
      // does not say what its cap is.
      if (response.status === 413) return { kind: 'too-large', bytes, limit: null }

      if (response.status === 409) {
        const parsed = await parseJson(response)
        // A `409` whose body cannot be read is still a Conflict, but there is
        // nothing to describe it with, so it must not be recorded as one — the
        // Resolution pane would have no bodies to offer. Reported as an answer
        // that did not fit the contract instead, and the next push meets the
        // same `409`.
        if (!('value' in parsed)) return parsed
        const record = asRecord(parsed.value)
        return record === null ? { kind: 'malformed' } : { kind: 'conflict', record }
      }

      if (response.status !== 200) return { kind: 'rejected', httpStatus: response.status }

      const parsed = await parseJson(response)
      if (!('value' in parsed)) return parsed
      const rev = isRecord(parsed.value) ? parsed.value.rev : null
      if (typeof rev !== 'number' || !Number.isSafeInteger(rev) || rev < 1) {
        // The body landed, but the revision it landed at is unknown — and a
        // guess would be recorded as the Last-Agreed Body and push against a
        // revision the server never issued.
        return { kind: 'malformed' }
      }

      return { kind: 'accepted', rev }
    },
  }
}
